import * as React from 'react';
import type { CityCode } from '@loci/city-editions';
import { cn } from '@/components/atoms/primitives/utils';
import { cityHostLabel, defaultCityCode } from '@/lib/cross-host-urls';
import { GLOBAL_NAV_ACCENT_VARS, GlobalNavCoordStrip } from './GlobalNavCoordStrip';
import { formatCoordDegree } from '@/utils/formatCoord';
import { formatCount } from '@/lib/format/count';

export interface CityCoordStripProps {
  /** City display name, e.g. "Charlotte". */
  cityName: string;
  /** Latitude in decimal degrees. Rendered to 4 places. */
  lat: number;
  /** Longitude in decimal degrees. Rendered to 4 places. */
  lng: number;
  /**
   * Catalog event count for the strip's events chip. Surfaced as
   * `<b>N EVENTS</b>` — WEB-486 relabelled the previous misleading "LIVE"
   * since this is the unwindowed catalog total, not an events-live-right-now
   * count. Omit to hide the chip. The prop keeps its `liveCount` name for
   * call-site back-compat.
   */
  liveCount?: number;
  /**
   * Optional meta eyebrow label rendered before the events count, e.g.
   * `THIS WEEK` (DS 1.5.0 city strip — `<span>THIS WEEK</span><b>N EVENTS</b>`,
   * `docs/design-system/1.5.0/templates/city/index.html` data-attr line 558).
   * LEFT UNSET by default and by every production call site: `liveCount` is the
   * unwindowed catalog total (WEB-486), so labelling it with a time window
   * (`THIS WEEK`) would fabricate a windowed count the data does not back. Pass
   * it only when a genuinely windowed count is threaded through (data gap
   * tracked for FRE-884). Storybook uses it for full DS-parity capture.
   */
  metaLabel?: string;
  /**
   * City code driving the `CITY · <host>` eyebrow (e.g. `CLT` →
   * `CITY · CLT.LOCI.CITY`). Defaults to the registry default city so existing
   * call sites keep working; multi-city surfaces should pass it explicitly.
   */
  cityCode?: CityCode;
  /**
   * Request-host staging flag from `x-is-staging` (WEB-776), threaded down by
   * {@link CityChromeView} from the root-layout `CityStagingProvider`.
   *
   * REQUIRED FOR HYDRATION SAFETY (WEB-824) wherever this strip renders inside
   * a client boundary — today, every mount under {@link CityChromeView}.
   * `CityChrome` is `'use client'`, so the strip executes during SSR *and*
   * again during hydration, while `resolveIsStaging()` falls back to
   * `window.location.hostname` when `NEXT_PUBLIC_LOCI_STAGE` is unset (the case
   * on the staging build) — a branch that only exists in the browser. Left to
   * resolve itself, the server rendered `CITY · CLT.LOCI.CITY` and the client
   * `CITY · STAGING-CLT.LOCI.CITY`: a text mismatch that threw React #418 and
   * made React discard the server tree and re-render the whole page
   * client-side. #418 is *recoverable*, so it ships as HTTP 200 with no error
   * digest — uptime checks, `next build` and `type-check` are all blind to it.
   * Passing the server-resolved flag makes the eyebrow a pure function of
   * props, so both passes agree byte-for-byte.
   *
   * Safe to omit only where the strip renders server-side ONCE, with no second
   * pass to disagree with: stories and unit tests. Artist and venue detail no
   * longer mount this strip (FRE-1333 / FRE-1332 — `CityChrome` owns it).
   * Those server-only mounts do still render the *production* host on staging
   * — a label defect, not a hydration one, tracked with the other unthreaded
   * sites in WEB-826.
   */
  isStaging?: boolean;
  /** Additional CSS classes. */
  className?: string;
}

function formatCoord(value: number): string {
  return value.toFixed(4);
}

/** `https://clt.loci.city/` → `CLT.LOCI.CITY` for the eyebrow. */
function eyebrowHostLabel(cityCode: CityCode, isStaging?: boolean): string {
  try {
    return cityHostLabel(cityCode, { isStaging }).toUpperCase();
  } catch {
    return `${cityCode.toLowerCase()}.loci.city`.toUpperCase();
  }
}

/**
 * CityCoordStrip — the city layer's top coordinate / meta strip (DS 1.5.0
 * `.o-globalnav__coord`, rendered by `loci-globalnav-mount.js` from the
 * `data-loci-gnav` block in
 * `docs/design-system/1.5.0/templates/city/index.html` lines 554–558, styled by
 * `docs/design-system/1.5.0/nav/loci-globalnav.css` lines 59–98).
 *
 * A fixed 26px mono band. Dark sits on `--n-950`; light (FRE-1356) uses
 * `--bg-sunken` so theme-resolved `--fg-*` ink stays readable.
 * `0.18em`, uppercase) with three space-between segments from 721px:
 *
 * - LEFT  host segment — 5px skyline-glow pulse dot + `CITY · CLT.LOCI.CITY`
 *         eyebrow in `--gn-accent-glow`.
 * - CENTER position segment — mono degree coords (`35.2271° N · 80.8431° W`).
 *         A visually-hidden span keeps the legacy `lat, lng` comma text + plain
 *         city name so external template assertions (`city-coord-strip-coords`,
 *         `city-coord-strip-city`) and assistive tech retain them.
 * - RIGHT meta segment — DS 1.5.0 `<span>THIS WEEK</span><b>N EVENTS</b>`
 *         when `liveCount` is provided. The `THIS WEEK` label only renders
 *         when `metaLabel` is supplied (see prop docs — production omits it).
 *
 * At ≤720px (WEB-621 / WEB-1136): the slot swaps coordinates ⇄ event count
 * every 6s so the 26px strip never reflows; one `data-face` attribute drives
 * both layers (WEB-1131). As in the DS template, the readout is left-set: on
 * the gutter ≤440 (host eyebrow hidden), right after the host label at
 * 441–720 (WEB-1134). From 721px coordinates and the count sit together,
 * right-grouped with a 16px gap. Under `prefers-reduced-motion: reduce` the
 * slot shows coordinates statically. Assistive tech reads one string
 * (`Charlotte · 35.2271° N, 80.8431° W · 2,002 events`).
 *
 * Built on the shared {@link GlobalNavCoordStrip} primitive (one shell across
 * apex / city / app — "fix once, never fork"). Ground is theme-delta via
 * `.city-coord-strip` in `styles/surfaces/city.css` (FRE-1356); not an ink-lock.
 *
 * Pure presentational server component — data flows in via props. The mobile
 * crossfade host is a client child (tab-visibility pause only).
 */
export function CityCoordStrip({
  cityName,
  lat,
  lng,
  liveCount,
  metaLabel,
  cityCode = defaultCityCode(),
  isStaging,
  className,
}: CityCoordStripProps): React.ReactElement {
  const showLive = typeof liveCount === 'number';
  const degreeCoords = `${formatCoordDegree(lat, 'lat')} · ${formatCoordDegree(lng, 'lng')}`;
  // WEB-1131: one accessible string for the whole strip — the visual layers
  // stack in one slot on mobile, so read separately they run together.
  const accessibleLabel = [
    cityName,
    `${formatCoordDegree(lat, 'lat')}, ${formatCoordDegree(lng, 'lng')}`,
    showLive ? `${formatCount(liveCount)} events` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <GlobalNavCoordStrip
      accessibleLabel={accessibleLabel}
      eyebrow={`CITY · ${eyebrowHostLabel(cityCode, isStaging)}`}
      testId="city-coord-strip"
      dotTestId="city-coord-strip-pulse-dot"
      eyebrowTestId="city-coord-strip-eyebrow"
      // FRE-1356: ground is `.city-coord-strip` in styles/surfaces/city.css (dark `--n-950`,
      // light `--bg-sunken`). Do not ink-lock `--n-950` here.
      className={cn('city-coord-strip', className)}
      // FRE-1362: coords + meta group on the right (design `.o-globalnav__coord-cycle`).
      groupCoordsRight
      position={
        <>
          {/* Plain city name — DS 1.5.0 shows coords ONLY in the center, but
              `city-coord-strip-city` textContent is an external template
              contract (CityHomeTemplate / ArtistDetailTemplate). Kept sr-only
              so the contract + AT hold without the visible name regressing the
              DS parity (which dropped it from the center segment). */}
          <span className="sr-only" data-testid="city-coord-strip-city">
            {cityName}
          </span>
          <span
            data-testid="city-coord-strip-coords"
            // `role="img"` makes the coords a single labelled unit: AT announces
            // the `aria-label` and skips the decorative degree glyphs. Without a
            // role, `aria-label` on a bare span is prohibited (axe
            // aria-prohibited-attr).
            //
            // `max-w-full truncate`: the parent position layer already
            // truncates, but this inner span is the nowrap degree string
            // and was painting past the 320 viewport when `liveCount` is
            // unset (city layout — no meta/crossfade host).
            role="img"
            aria-label={`Coordinates ${formatCoord(lat)}, ${formatCoord(lng)}`}
            className="inline-block max-w-full truncate"
          >
            <span aria-hidden="true">{degreeCoords}</span>
            {/* Legacy comma-separated coordinates — visually hidden, kept for
                assistive tech + downstream template assertions. */}
            <span className="sr-only">
              {formatCoord(lat)}, {formatCoord(lng)}
            </span>
          </span>
        </>
      }
      positionTestId="city-coord-strip-position"
      meta={
        showLive ? (
          <span
            className="city-coord-strip__pulse inline-flex items-center"
            data-testid="city-coord-strip-pulse"
          >
            <span
              data-testid="city-coord-strip-pulse-label"
              className="inline-flex items-center gap-2"
            >
              {metaLabel != null ? (
                // Hidden below `sm` so the count never overflows the band on
                // the narrowest (320px) target once the center coords collapse.
                <span className="hidden sm:inline">{metaLabel}</span>
              ) : null}
              {/* DS 1.5.0 source emphasizes the whole `N EVENTS`
                  (`<b>684 EVENTS</b>` → `--fg-2` medium via the primitive's
                  `[&_b]`), not just the count. WEB-1131 (P0-3): grouped via
                  the pinned-locale `formatCount` (`2,002`, never `2002`). */}
              <b>{formatCount(liveCount)} EVENTS</b>
            </span>
          </span>
        ) : undefined
      }
      metaTestId="city-coord-strip-meta"
      // City layer accent — the strip is also mounted standalone by detail
      // templates (artist / venue), where no accent-bearing header wraps it,
      // so it carries its own layer vars (drives the pulse-dot skyline glow).
      style={GLOBAL_NAV_ACCENT_VARS.city}
    />
  );
}
